import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * ClientsKpiTiles — the 4-column KPI strip rendered at the top of the
 * `/clients` page (T-0108 / C20-130).
 *
 * Mirrors the four-tile layout in `design_handoff_caliber_v2/prototype/
 * screens-gaps.js → Screens.clients`:
 *
 *   ┌────────────┬────────────┬────────────┬────────────┐
 *   │ Total      │ Active     │ Avg/client │ Sectors    │
 *   │ clients    │ engagements│            │ served     │
 *   └────────────┴────────────┴────────────┴────────────┘
 *
 * Each tile carries:
 *   - 12px / 500 uppercase ink-500 label
 *   - 28px / 600 ink-900 value (tabular-nums)
 *   - optional 11px / 500 ink-500 trend line
 *
 * The prototype's KPI tile design does NOT include a `<KpiSourcePill>` —
 * the source pill is reserved for engagement-level fact strips and
 * scorecard tiles. The `source` field is kept on the interface for
 * documentation but is no longer rendered.
 */
export interface ClientsKpiTile {
  /** Short label, e.g. "Total clients". Rendered in uppercase. */
  label: string;
  /** Headline value. Pass a string so consumers control formatting. */
  value: React.ReactNode;
  /** Optional supporting trend line ("3 active · 2 archived"). */
  trend?: React.ReactNode;
  /** Source attribution kept for documentation; not rendered per prototype. */
  source?: string;
  /** When true, the tile renders the pending dashes + muted ink. */
  pending?: boolean;
}

export interface ClientsKpiTilesProps extends React.HTMLAttributes<HTMLDivElement> {
  tiles: ReadonlyArray<ClientsKpiTile>;
}

export function ClientsKpiTiles({ tiles, className, ...rest }: ClientsKpiTilesProps) {
  return (
    <div
      className={cn(
        // 4-col grid, paper-0 bg, ink-100 border, soft navy box-shadow.
        // Mirrors the geometry of the engagement-overview FactsStrip but
        // without the hero overlap (no `-mt-2`).
        'grid grid-cols-1 overflow-hidden rounded-large bg-paper-0 sm:grid-cols-2 lg:grid-cols-4',
        'border border-ink-100',
        'shadow-[0_6px_20px_rgb(24_46_88_/_0.06)]',
        className,
      )}
      {...rest}
    >
      {tiles.map((tile, idx) => (
        <Tile key={`${tile.label}-${idx}`} tile={tile} isLast={idx === tiles.length - 1} />
      ))}
    </div>
  );
}

function Tile({ tile, isLast }: { tile: ClientsKpiTile; isLast: boolean }) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-[4px] px-5 py-4',
        // Right-border separator between tiles except the last; on small
        // screens the grid wraps so the right-border becomes the bottom
        // border via the grid gap. The wrapper's border + grid handles
        // both cases without extra rules.
        !isLast && 'lg:border-r lg:border-ink-100',
      )}
    >
      <div className="text-[12px] font-semibold uppercase tracking-[0.06em] text-ink-500">
        {tile.label}
      </div>
      <div
        className={cn(
          'text-[28px] font-semibold leading-[1.1] tracking-[-0.005em] tabular-nums',
          tile.pending ? 'text-ink-300' : 'text-ink-900',
        )}
      >
        {tile.value}
      </div>
      {tile.trend ? (
        <div className="text-[11px] leading-[1.3] text-ink-500">{tile.trend}</div>
      ) : null}
    </div>
  );
}

ClientsKpiTiles.displayName = 'ClientsKpiTiles';
